import { EconomyTemplate } from '@/types/economyTemplate';
import { UserRole } from '@/types';
import { useAuth } from '@/hooks/useAuth';
import {
  ECONOMY_TEMPLATE_VISIBILITY,
  getEconomyTemplateTypeLabel,
} from '@/utils/economyTemplateLabels';
import { getScenarioUnitLabel } from '@/utils/scenarioUnits';
import {
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Category as CategoryIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

interface TemplateCardProps {
  template: EconomyTemplate;
  onClick?: (template: EconomyTemplate) => void;
  onEdit?: (template: EconomyTemplate) => void;
  onCopy?: (template: EconomyTemplate) => void;
  onDelete?: (template: EconomyTemplate) => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onClick,
  onEdit,
  onCopy,
  onDelete,
}) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.ADMIN || user?.role === 'admin';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const visibilityStatusId = template.visibility_status_id;
  const isPublic = visibilityStatusId === ECONOMY_TEMPLATE_VISIBILITY.PUBLIC;
  const isCompanyPublic = visibilityStatusId === ECONOMY_TEMPLATE_VISIBILITY.COMPANY_PUBLIC;

  const isFromUserOrg = template.organization_id === user?.organization_id;
  const canEdit = isFromUserOrg || (isPublic && isSuperAdmin);
  const canDelete = isFromUserOrg || (isPublic && isSuperAdmin);
  const canCopy = true;
  const canShowMenu = canCopy || canEdit || canDelete;

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const getVisibilityChip = () => {
    if (isPublic) {
      return {
        icon: <PublicIcon />,
        label: 'Public',
        color: 'success' as const,
      };
    }
    if (isCompanyPublic) {
      return {
        icon: <BusinessIcon />,
        label: 'Company Public',
        color: 'info' as const,
      };
    }
    return {
      icon: <LockIcon />,
      label: 'Private',
      color: 'default' as const,
    };
  };

  const visibilityChip = getVisibilityChip();

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: onClick ? 'translateY(-4px)' : 'none',
          boxShadow: onClick ? 4 : 1,
        },
      }}
      onClick={() => onClick?.(template)}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {canShowMenu && (
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setAnchorEl(e.currentTarget);
              }}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" component="h3" gutterBottom sx={{ pr: 4 }}>
            {template.name}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={visibilityChip.icon}
              label={visibilityChip.label}
              size="small"
              color={visibilityChip.color}
              variant="outlined"
            />
            <Chip
              icon={<CategoryIcon />}
              label={getEconomyTemplateTypeLabel(template.template_type)}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip label={template.currency} size="small" variant="outlined" />
            <Chip
              label={getScenarioUnitLabel(template.unit_id)}
              size="small"
              variant="outlined"
            />
          </Box>

          {template.description?.trim() && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {template.description.trim()}
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 2, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
              <PersonIcon fontSize="small" />
            </Avatar>
            <Typography variant="body2" color="text.secondary">
              Author: <strong>{template.author_name || 'Unknown'}</strong>
            </Typography>
          </Box>

          {template.editor_name && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'success.main' }}>
                <EditIcon fontSize="small" />
              </Avatar>
              <Typography variant="body2" color="text.secondary">
                Editor: <strong>{template.editor_name}</strong>
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CalendarIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
            <Typography variant="caption" color="text.secondary">
              Created: <strong>{formatDate(template.created_at)}</strong>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarIcon sx={{ fontSize: 16, mr: 1, color: 'warning.main' }} />
            <Typography variant="caption" color="text.secondary">
              Updated: <strong>{formatDate(template.updated_at)}</strong>
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
      >
        {canCopy && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onCopy?.(template);
            }}
          >
            <CopyIcon fontSize="small" sx={{ mr: 1 }} />
            Copy
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onEdit?.(template);
            }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onDelete?.(template);
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};

export default TemplateCard;
